import { PaginationProps, TableProps } from 'antd';
import { getApi } from 'apis';
import PopUpWrapper from 'components/Global/PopUpWrapper';
import TableActionHeader from 'components/Global/TableActionHeader';
import ViewPopUpWrapper from 'components/Global/ViewPopUpWrapper';
import dayjs from 'dayjs';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { toastText, checkPermission } from 'utils/utils';
import { useDebounce } from 'utils/hooks';
import EditMedicinesBody from './EditMedicinesBody';
import CustomTable from './CustomTable';
import ViewMedicinesBody from './ViewMedicinesBody';


const RejectedMedicines = () => {
	// State variables
	const [title, setTitle] = useState('Add Medicines');
	const [search, setSearch] = useState('');
	const [page, setPage] = useState(1);
	const [limit, setLimit] = useState(
		Number(localStorage.getItem('medicineTableSize')) || 10
	);
	const [filter, setFilter] = useState('');
	const [sort, setSort] = useState('');
	const [type, setType] = useState('asc');
	const [editSelectedMedicine, setEditSelectedMedicine] = useState<any>();
	const [totalMedicines, setTotalMedicines] = useState(0);
	const [medicinesData, setMedicinesData] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [isPoppedUp, SetIsPoppedUp] = useState<boolean>(false);
	const [allUsers, setAllUsers] = useState([]);
	const [selectedUser, setSelectedUser] = useState(null);
	const [selectedDate, setSelectedDate] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);
	const [isViewPopUpOpen, setIsViewPopUpOpen] = useState(false);
	const [isInitialized, setIsInitialized] = useState(false);

	const debouncedSearch = useDebounce(search, 500);

	const medicinesPermissions = useSelector(
		(state: any) => state?.userProfile?.data
	);
	const viewHandler = (medicine: any) => {
		setEditSelectedMedicine(medicine);
		setTitle('Medicine Details');
		setIsViewPopUpOpen(true);
	};

	const [optionsState, setOptionsState] = useState({
		medicineName: [],
		manufacturer: [],
		marketedBy: [],
		weightage: [],
		numberOfItems: [],
		flavors: [],
		offers: [],
		saltComposition: [],
		usesTags: [],
	});
	const openDrawerHandler = () => {
		SetIsPoppedUp(true)
	};

	const closeDrawerByAnimation = () => {
		SetIsPoppedUp(false)
		setOptionsState({
			medicineName: [],
			manufacturer: [],
			marketedBy: [],
			weightage: [],
			numberOfItems: [],
			flavors: [],
			offers: [],
			saltComposition: [],
			usesTags: [],
		})
		setEditSelectedMedicine(null)
	};

	const performSearchHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setSearch(value);
	};

	useEffect(() => {
		setPage(1);
	}, [debouncedSearch]);

	const fetchMedicines = useCallback(async () => {
		setIsLoading(true);
		const query = {
			page: page,
			limit: limit,
			search: debouncedSearch,
			sort: sort,
			type: type,
			filter: filter,
			userId: selectedUser,
			selectedDate,
			isQualityCheck: false
		};
		try {
			const res = await getApi('/medicine/get-all', query);
			setMedicinesData(res?.data?.data);
			setTotalMedicines(res?.data?.total);
		} catch (error: any) {
			toastText(error?.response?.data?.error?.errorDescription, 'error');
		}
		finally {
			setIsLoading(false);
		}
	}, [page, limit, debouncedSearch, type, sort, filter, selectedUser, selectedDate]);

	const handleTableChange: TableProps<any>['onChange'] = (
		pagination,
		filters,
		sorter
	) => {
		const sortOrder = Array.isArray(sorter)
			? sorter[0]?.order
			: sorter.order === 'ascend'
				? 'asc'
				: 'desc';
		setType(sortOrder as string);

		const sortField = Array.isArray(sorter)
			? sorter[0]?.field
			: sorter.field === 'name'
				? 'firstName'
				: sorter.field;
		setSort(sortField as string);
	};

	const onShowSizeChange: PaginationProps['onShowSizeChange'] = (
		current,
		pageSize
	) => {
		setPage(current);
		setLimit(pageSize);
	};

	const isRejectedViewAll = checkPermission(medicinesPermissions?.permissions, {
		permissionName: 'Rejected',
		permission: ['viewAll'],
	});
	const canViewAllUsers = medicinesPermissions?.isAdmin === true || isRejectedViewAll;

	const fetchAllUsers = async () => {
		if (canViewAllUsers) {
			await getApi('/user').then((res: any) => {
				if (res?.data?.responseStatus === 200) {
					const users = res?.data?.data;

					const filteredUsers = users?.map((item: any) => ({
						userId: item.userId,
						name: item.name,
					}));

					setAllUsers(filteredUsers);
				}
			}).catch((err: any) => {
			})
		}
	}

	useEffect(() => {
		if (medicinesPermissions && !isInitialized) {
			if (canViewAllUsers) {
				setSelectedUser(null);
			} else {
				setSelectedUser(medicinesPermissions?.id);
			}
			setIsInitialized(true);
		}
	}, [medicinesPermissions, isInitialized, canViewAllUsers]);

	useEffect(() => {
		if (isInitialized) {
			fetchMedicines();
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps -- fetchMedicines is stable
	}, [limit, page, debouncedSearch, sort, type, filter, selectedUser, selectedDate, isInitialized]);

	useEffect(() => {
		if (canViewAllUsers) {
			fetchAllUsers();
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps -- fetchAllUsers is stable
	}, [canViewAllUsers]);

	return (
		<>
			<div >
				<TableActionHeader title="Medicines">
				</TableActionHeader>

				<CustomTable
					dataSource={medicinesData}
					handleTableChange={handleTableChange}
					onShowSizeChange={onShowSizeChange}
					isLoading={isLoading}
					search={search}
					totalRecords={totalMedicines}
					performSearchHandler={performSearchHandler}
					performFilterHandler={(value: any) => setFilter(value)}
					openDrawerHandler={openDrawerHandler}
					setEditSelectedMedicine={setEditSelectedMedicine}
					currentPage={page}
					pageSize={limit}
					setSearch={setSearch}
					setTitle={setTitle}
					allUsers={allUsers}
					setSelectedUser={setSelectedUser}
					selectedUser={selectedUser}
					selectedDate={selectedDate}
					setSelectedDate={setSelectedDate}
					viewHandler={viewHandler}
				/>
			</div>

			{isPoppedUp &&
				<PopUpWrapper
					title={title}
					isPoppedUp={isPoppedUp}
					closeDrawerByAnimation={closeDrawerByAnimation}
				>
					<EditMedicinesBody
						closeDrawerByAnimation={closeDrawerByAnimation}
						editSelectedMedicine={editSelectedMedicine}
						setEditSelectedMedicine={setEditSelectedMedicine}
						fetchMedicines={fetchMedicines}
						setOptionsState={setOptionsState}
						optionsState={optionsState}
					/>
				</PopUpWrapper>
			}
			<div>
				{isViewPopUpOpen && (
					<ViewPopUpWrapper
						title={title}
						isPoppedUp={isViewPopUpOpen}
						closeDrawerByAnimation={() => {
							setIsViewPopUpOpen(false);
							setEditSelectedMedicine(null);
						}}
					>
						<ViewMedicinesBody
							medicine={editSelectedMedicine}
						/>
					</ViewPopUpWrapper>
				)}
			</div>


		</>
	);
};

export default RejectedMedicines;